import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Buildings, CheckCircle, MagnifyingGlass } from 'phosphor-react';
import { ICompany, useCompanies } from '../../../../../hooks/useCompanies';
import {
  IContratoSocietario,
  StatusContratoSocietario,
  useSocietario,
} from '../../../../../hooks/useSocietario';
import { IWizardFormValues } from '../schema';

const statusLabels: Record<StatusContratoSocietario, string> = {
  rascunho_inicial: 'Inicial',
  rascunho: 'Rascunho',
  campos_pendentes: 'Campos pendentes',
  em_revisao: 'Em revisão',
  gerado: 'Gerado',
  assinado: 'Assinado',
  cancelado: 'Cancelado',
  arquivado: 'Arquivado',
};

const statusColors: Record<StatusContratoSocietario, string> = {
  rascunho_inicial: 'yellow',
  rascunho: 'gray',
  campos_pendentes: 'orange',
  em_revisao: 'red',
  gerado: 'blue',
  assinado: 'green',
  cancelado: 'red',
  arquivado: 'gray',
};

const formatDate = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
};

// Sanitiza valor digitado para placeholder do extrator do contrato vigente
// (mesmo padrao do `aplicarPreFillDoVigente`). Evita pre-popular com "[A DEFINIR]".
const isPlaceholderText = (v: unknown): boolean =>
  typeof v === 'string' && /^\s*\[\s*a\s+definir\s*\]\s*$/i.test(v);

/**
 * Step novo (sessao 2026-05-08, Fase B do plano de 4 features): permite
 * que o operador busque empresas ja cadastradas por nome / razao social /
 * external_id (Codiemp) / CNPJ ANTES de continuar o wizard, evitando
 * criar contrato duplicado pra empresa que ja tem historico.
 *
 * UX: busca com debounce -> lista compacta -> ao selecionar, mostra
 * historico de contratos daquela empresa (cards mini) + bota botao
 * "Usar essa empresa" que pre-popula o form.
 *
 * Nao bloqueia o avanco: operador pode pular se a empresa for nova.
 */
interface StepVerificacaoEmpresaProps {
  /**
   * Callback do pai pra avancar pro proximo step do wizard. Disparado
   * quando o operador clica "Continuar sem empresa cadastrada" — limpa
   * a selecao e avanca em uma so acao (sessao 2026-05-11).
   */
  onContinuar?: () => void;
}

export const StepVerificacaoEmpresa: React.FC<StepVerificacaoEmpresaProps> = ({ onContinuar }) => {
  const { setValue, watch } = useFormContext<IWizardFormValues>();
  const { getCompanies } = useCompanies();
  const { getContratos } = useSocietario();

  const empresaIdSelecionada = watch('empresa_id');

  // Padrao alinhado com a tela de listagem de empresas (sessao 2026-05-08
  // — print do usuario): Select com 3 modos de busca + Input com
  // placeholder dinamico. Backend aceita os 3 filtros via
  // `EmpresaQueryParams` (`external_id` pra Codiemp, `nome`,
  // `cnpj`/`razao_social`). Codiemp e o `external_id` da TbEmpresa.
  type ModoBusca = 'codiemp' | 'nome' | 'cnpj';
  const MODO_LABELS: Record<ModoBusca, string> = {
    codiemp: 'ID',
    nome: 'Nome',
    cnpj: 'CNPJ',
  };
  const MODO_PLACEHOLDERS: Record<ModoBusca, string> = {
    codiemp: 'Buscar por ID (Codiemp)…',
    nome: 'Buscar por Nome…',
    cnpj: 'Buscar por CNPJ…',
  };

  const [modo, setModo] = useState<ModoBusca>('nome');
  const [termo, setTermo] = useState('');
  const [debouncedTermo, setDebouncedTermo] = useState('');
  const [empresaSelecionada, setEmpresaSelecionada] = useState<ICompany | null>(null);

  // Debounce simples (300ms) — evita request a cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTermo(termo.trim()), 300);
    return () => clearTimeout(t);
  }, [termo]);

  const buscaParams = useMemo(() => {
    const base: Record<string, any> = { page: 1, size: 10 };
    if (!debouncedTermo) return base;
    if (modo === 'codiemp') base.external_id = debouncedTermo;
    else if (modo === 'cnpj') base.cnpj = debouncedTermo;
    else base.nome = debouncedTermo;
    return base;
  }, [debouncedTermo, modo]);

  const { data: dataBusca, isFetching: isFetchingBusca } = useQuery({
    queryKey: ['empresas-search-societario', buscaParams],
    queryFn: getCompanies,
    // Codiemp/CNPJ podem ser bem curtos (1-3 chars) — habilita a partir de 1 char.
    // Nome usa >=2 pra evitar request a cada letra.
    enabled: modo === 'nome' ? debouncedTermo.length >= 2 : debouncedTermo.length >= 1,
  });

  const empresas: ICompany[] = dataBusca?.data?.items ?? [];

  // Quando seleciona uma empresa, busca o historico dela.
  const histParams = useMemo(
    () => ({ empresa_id: empresaSelecionada?.id ?? 0, page: 1, size: 20 }),
    [empresaSelecionada?.id],
  );
  const { data: dataHist, isFetching: isFetchingHist } = useQuery({
    queryKey: ['societario-contratos-empresa', histParams],
    queryFn: getContratos,
    enabled: !!empresaSelecionada?.id,
  });
  const historico: IContratoSocietario[] = dataHist?.data?.items ?? [];

  const handleSelecionar = (e: ICompany) => {
    setEmpresaSelecionada(e);
  };

  const handleUsarEmpresa = () => {
    if (!empresaSelecionada) return;
    setValue('empresa_id', empresaSelecionada.id, { shouldDirty: true });
    // Em ALTERAÇÃO, a fonte da verdade dos dados da empresa é o CONTRATO VIGENTE (upload no
    // passo seguinte) — não o registro TbEmpresa, que pode estar desatualizado. Selecionar a
    // empresa aqui só LINKA (empresa_id) + mostra o histórico; NÃO pré-preenche razão/CNPJ/
    // fantasia (senão, se a extração do vigente falhar num campo, sobra dado stale da empresa).
    // Em constituição não há vigente → o pré-preenchimento a partir da empresa é útil.
    if (watch('tipo_operacao') === 'alteracao') return;
    if (empresaSelecionada.razao_social && !isPlaceholderText(empresaSelecionada.razao_social)) {
      setValue('razao_social', empresaSelecionada.razao_social, { shouldDirty: true });
    }
    if (empresaSelecionada.nome && !isPlaceholderText(empresaSelecionada.nome)) {
      setValue('nome_fantasia', empresaSelecionada.nome, { shouldDirty: true });
    }
    if (empresaSelecionada.cnpj && !isPlaceholderText(empresaSelecionada.cnpj)) {
      setValue('cnpj', empresaSelecionada.cnpj, { shouldDirty: true });
    }
  };

  const handleContinuarSemCadastro = () => {
    setEmpresaSelecionada(null);
    setValue('empresa_id', null, { shouldDirty: true });
    // Avanca o wizard se o pai forneceu callback (esperado em uso normal).
    // Sem callback (uso isolado em storybook/test), so limpa o state.
    if (onContinuar) {
      onContinuar();
    }
  };

  const renderResultadoBusca = () => {
    if (isFetchingBusca) {
      return (
        <Center py={4}>
          <Spinner size="sm" color="blue.500" />
        </Center>
      );
    }
    if (empresas.length === 0) {
      return (
        <Center py={4}>
          <Text fontSize="sm" color="gray.500">
            Nenhuma empresa encontrada para {MODO_LABELS[modo].toLowerCase()} “{debouncedTermo}”.
          </Text>
        </Center>
      );
    }
    return (
      <Stack spacing={1} divider={<Box h="1px" bg="gray.100" />}>
        {empresas.map(e => {
          const selecionada = empresaSelecionada?.id === e.id;
          return (
            <Flex
              key={e.id}
              p={3}
              borderRadius="md"
              cursor="pointer"
              bg={selecionada ? 'blue.50' : 'transparent'}
              _hover={{ bg: selecionada ? 'blue.50' : 'gray.50' }}
              onClick={() => handleSelecionar(e)}
              align="center"
              justify="space-between"
              gap={3}
            >
              <Flex align="center" gap={3} flex={1} minW={0}>
                <Buildings size={20} color="#1B88F5" weight="duotone" />
                <Box minW={0} flex={1}>
                  <Text fontWeight="600" fontSize="sm" noOfLines={1}>
                    {e.razao_social || e.nome || 'Sem razão social'}
                  </Text>
                  <Flex gap={2} fontSize="xs" color="gray.500" flexWrap="wrap">
                    {e.cnpj && <Text>CNPJ: {e.cnpj}</Text>}
                    {e.external_id && <Text>Codiemp: {e.external_id}</Text>}
                  </Flex>
                </Box>
              </Flex>
              {selecionada && <CheckCircle size={20} color="#22C55E" weight="fill" />}
            </Flex>
          );
        })}
      </Stack>
    );
  };

  const renderHistorico = () => {
    if (isFetchingHist) {
      return (
        <Center py={3}>
          <Spinner size="sm" color="green.600" />
        </Center>
      );
    }
    if (historico.length === 0) {
      return (
        <Text fontSize="sm" color="gray.500" fontStyle="italic">
          Sem contratos anteriores. Esta será a primeira geração para esta empresa.
        </Text>
      );
    }
    return (
      <Stack spacing={2}>
        {historico.map(c => (
          <Flex
            key={c.id}
            bg="white"
            p={3}
            borderRadius="md"
            align="center"
            justify="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <Flex align="center" gap={2} flex={1} minW={0}>
              <Text fontSize="sm" fontWeight="700" color="gray.700">
                #{c.id}
              </Text>
              <Badge
                colorScheme={c.tipo_operacao === 'constituicao' ? 'blue' : 'purple'}
                fontSize="9px"
              >
                {c.tipo_operacao === 'constituicao' ? 'Constituição' : 'Alteração'}
              </Badge>
              <Badge colorScheme={statusColors[c.status]} fontSize="9px">
                {statusLabels[c.status]}
              </Badge>
            </Flex>
            <Text fontSize="xs" color="gray.500">
              {formatDate(c.dt_criacao)}
            </Text>
          </Flex>
        ))}
      </Stack>
    );
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontWeight="bold" fontSize="lg">
          Verificação de empresa
        </Text>
        <Text fontSize="sm" color="gray.600" mt={1}>
          Busque a empresa pelo nome, razão social, CNPJ ou Codiemp para verificar contratos já
          existentes antes de continuar. Evita duplicidade.
        </Text>
      </Box>

      <Flex gap={2} align="stretch">
        <Select
          value={modo}
          onChange={e => {
            setModo(e.target.value as ModoBusca);
            // Limpa termo ao trocar modo — formatos sao incompativeis (CNPJ
            // numerico vs Nome texto) e search anterior nao se aplica.
            setTermo('');
          }}
          maxW="120px"
          bg="white"
          aria-label="Tipo de busca"
        >
          <option value="codiemp">{MODO_LABELS.codiemp}</option>
          <option value="nome">{MODO_LABELS.nome}</option>
          <option value="cnpj">{MODO_LABELS.cnpj}</option>
        </Select>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <MagnifyingGlass size={18} />
          </InputLeftElement>
          <Input
            placeholder={MODO_PLACEHOLDERS[modo]}
            value={termo}
            onChange={e => setTermo(e.target.value)}
            autoFocus
            bg="white"
          />
        </InputGroup>
      </Flex>

      {(modo === 'nome' ? debouncedTermo.length >= 2 : debouncedTermo.length >= 1) && (
        <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={2}>
          {renderResultadoBusca()}
        </Box>
      )}

      {empresaSelecionada && (
        <Box bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="md" p={4}>
          <Flex justify="space-between" align="flex-start" gap={3} mb={3}>
            <Box>
              <Text fontWeight="bold" fontSize="sm" color="green.800" mb={1}>
                Empresa selecionada
              </Text>
              <Text fontWeight="600">
                {empresaSelecionada.razao_social || empresaSelecionada.nome}
              </Text>
              <Flex gap={3} fontSize="xs" color="gray.600" flexWrap="wrap" mt={1}>
                {empresaSelecionada.cnpj && <Text>CNPJ: {empresaSelecionada.cnpj}</Text>}
                {empresaSelecionada.external_id && (
                  <Text>Codiemp: {empresaSelecionada.external_id}</Text>
                )}
              </Flex>
            </Box>
            <Button
              colorScheme="green"
              size="sm"
              onClick={handleUsarEmpresa}
              isDisabled={empresaIdSelecionada === empresaSelecionada.id}
            >
              {empresaIdSelecionada === empresaSelecionada.id
                ? 'Empresa em uso'
                : 'Usar essa empresa'}
            </Button>
          </Flex>

          <Box>
            <Text
              fontSize="2xs"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={2}
            >
              Histórico de contratos
            </Text>
            {renderHistorico()}
          </Box>
        </Box>
      )}

      {/* Botao de escape: continuar sem cadastro existente. Util pra
          constituicao de nova empresa que nao esta cadastrada. */}
      <Flex justify="flex-end">
        <Button variant="ghost" size="sm" onClick={handleContinuarSemCadastro} color="gray.600">
          Continuar sem empresa cadastrada
        </Button>
      </Flex>
    </VStack>
  );
};

export default StepVerificacaoEmpresa;
